import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { formatTime, formattedNum, urls } from '../../utils'
import { useMedia } from 'react-use'
import { useCurrentCurrency } from '../../contexts/Application'
import { RowFixed, RowBetween } from '../Row'

import LocalLoader from '../LocalLoader'
import { Box, Flex, Text } from 'rebass'
import Link from '../Link'
import { Divider, EmptyCard } from '..'
import DropdownSelect from '../DropdownSelect'
import FormattedName from '../FormattedName'
import { TYPE } from '../../Theme'
import { updateNameData } from '../../utils/data'
import getEnvironmentVar from '../../utils/getEnvironmentVar'
import isBlackListed from '../../utils/blacklist-token-list'
import { useEnergiswapRouterImplAddress } from '../../contexts/GlobalData'
import {
  PRESET_RANGES,
  DEFAULT_PRESET,
  computePresetRange,
  startOfDayUnix,
  endOfDayExclusiveUnix,
  isValidRange,
} from '../../utils/dateRangePresets'
import DatePicker from '../DatePicker'

dayjs.extend(utc)

const EXPLORER_URL = getEnvironmentVar('EXPLORER_URL')

const PageButtons = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 2em;
  margin-bottom: 0.5em;
`

const Arrow = styled.div`
  color: #34b7f1;
  opacity: ${(props) => (props.faded ? 0.3 : 1)};
  padding: 0 20px;
  user-select: none;
  :hover {
    cursor: pointer;
  }
`

const List = styled(Box)`
  -webkit-overflow-scrolling: touch;
`

const DashGrid = styled.div`
  display: grid;
  grid-gap: 1em;
  grid-template-columns: 100px 1fr 1fr;
  grid-template-areas: 'txn value time';

  > * {
    justify-content: flex-end;
    width: 100%;

    &:first-child {
      justify-content: flex-start;
      text-align: left;
      width: 100px;
    }
  }

  @media screen and (min-width: 500px) {
    > * {
      &:first-child {
        width: 180px;
      }
    }
  }

  @media screen and (min-width: 780px) {
    max-width: 1320px;
    grid-template-columns: 1.2fr 1fr 1fr 1fr 1fr;
    grid-template-areas: 'txn value amountToken amountOther time';

    > * {
      &:first-child {
        width: 180px;
      }
    }
  }

  @media screen and (min-width: 1080px) {
    max-width: 1320px;
    grid-template-columns: 1.2fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-areas: 'txn value amountToken amountOther account time';
  }
`

const ClickableText = styled(Text)`
  color: ${({ theme }) => theme.text1};
  user-select: none;
  text-align: end;

  &:hover {
    cursor: pointer;
    opacity: 0.6;
  }

  @media screen and (max-width: 640px) {
    font-size: 14px;
  }
`

const DataText = styled(Flex)`
  align-items: center;
  text-align: right;
  color: ${({ theme }) => theme.text1};

  & > * {
    font-size: 1em;
  }

  @media screen and (max-width: 40em) {
    font-size: 0.85rem;
  }
`

const SortText = styled.button`
  cursor: pointer;
  font-weight: ${({ active, theme }) => (active ? 500 : 400)};
  margin-right: 0.75rem !important;
  border: none;
  background-color: transparent;
  font-size: 1rem;
  padding: 0px;
  color: ${({ active, theme }) => (active ? theme.text1 : theme.text3)};
  outline: none;

  @media screen and (max-width: 600px) {
    font-size: 14px;
  }
`

const SORT_FIELD = {
  VALUE: 'amountUSD',
  AMOUNT0: 'token0Amount',
  AMOUNT1: 'token1Amount',
  TIMESTAMP: 'timestamp',
}

const TXN_TYPE = {
  ALL: 'All',
  SWAP: 'Swaps',
  ADD: 'Adds',
  REMOVE: 'Removes',
}

const ITEMS_PER_PAGE = 25

const FilterPanel = styled.div`
  background: ${({ theme }) => theme.bg2};
  border: 1px solid ${({ theme }) => theme.bg3};
  border-radius: 12px;
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
`

const FilterRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  flex-wrap: wrap;
`

const FilterGroup = styled(Flex)`
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const FilterLabel = styled.span`
  color: ${({ theme }) => theme.text3};
  font-size: 0.8rem;
  font-weight: 500;
  margin-right: 0.15rem;
`

const SmallButton = styled.button`
  cursor: pointer;
  background: ${({ theme }) => theme.bg3};
  color: ${({ theme }) => theme.text1};
  border: none;
  border-radius: 8px;
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const PresetButton = styled(SmallButton)`
  background: ${({ theme, active }) => (active ? theme.primary1 ?? '#2172E5' : theme.bg3)};
  color: ${({ active }) => (active ? '#FFFFFF' : undefined)};
`

function getTransactionType(event, symbol0, symbol1) {
  const formattedS0 = symbol0?.length > 8 ? symbol0.slice(0, 7) + '...' : symbol0
  const formattedS1 = symbol1?.length > 8 ? symbol1.slice(0, 7) + '...' : symbol1
  switch (event) {
    case TXN_TYPE.ADD:
      return 'Add ' + formattedS0 + ' and ' + formattedS1
    case TXN_TYPE.REMOVE:
      return 'Remove ' + formattedS0 + ' and ' + formattedS1
    case TXN_TYPE.SWAP:
      return 'Swap ' + formattedS0 + ' for ' + formattedS1
    default:
      return ''
  }
}

// @TODO rework into virtualized list
function TxnList({
  transactions,
  feedKey,
  hasMore,
  onLoadMore,
  isDateRangeFiltered,
  onSetDateRange,
  onClearDateRange,
  direction,
  onSetDirection,
  isLoadingInitial,
  symbol0Override,
  symbol1Override,
  color,
}) {
  // create transactions that have no blacklisted tokens:
  if (transactions) {
    if (transactions.burns) {
      transactions.burns = transactions.burns.filter(
        (item) =>
          !isBlackListed(item.pair?.token0?.symbol, item.pair?.token0?.id) &&
          !isBlackListed(item.pair?.token1?.symbol, item.pair?.token1?.id)
      )
    }
    if (transactions.mints) {
      transactions.mints = transactions.mints.filter(
        (item) =>
          !isBlackListed(item.pair?.token0?.symbol, item.pair?.token0?.id) &&
          !isBlackListed(item.pair?.token1?.symbol, item.pair?.token1?.id)
      )
    }
    if (transactions.swaps) {
      transactions.swaps = transactions.swaps.filter(
        (item) =>
          !isBlackListed(item.pair?.token0?.symbol, item.pair?.token0?.id) &&
          !isBlackListed(item.pair?.token1?.symbol, item.pair?.token1?.id)
      )
    }
  }

  // page state
  const [page, setPage] = useState(1)
  const [maxPage, setMaxPage] = useState(1)

  // EnergiswapRouterGovernProxy address
  const energiswapRouterImplAddress = useEnergiswapRouterImplAddress()

  // sorting
  const [sortDirection, setSortDirection] = useState(direction !== 'asc')
  const [sortedColumn, setSortedColumn] = useState(SORT_FIELD.TIMESTAMP)
  const [filteredItems, setFilteredItems] = useState()
  const [txFilter, setTxFilter] = useState(TXN_TYPE.ALL)

  const [currency] = useCurrentCurrency()
  const [loadingMore, setLoadingMore] = useState(false)
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [activePreset, setActivePreset] = useState(null)
  // Unix-second bounds of the currently applied date range, kept alongside the
  // day-granularity fromDate/toDate so toggling sort direction can re-fetch the exact
  // same range without recomputing it (and without a preset's range drifting off "now").
  const [appliedRange, setAppliedRange] = useState(null)
  // React state updates aren't synchronous, so a fast double-click/rapid-fire on "Next" can read
  // stale `loadingMore` before the first click's setLoadingMore(true) commits. This ref is set
  // synchronously so a second overlapping call is blocked immediately, not on the next render.
  const loadingMoreRef = useRef(false)

  // Only reset back to page 1 when we're looking at a genuinely different feed
  // (a different token/pair/account) - NOT every time `transactions` grows from a load-more fetch.
  useEffect(() => {
    setMaxPage(1) // edit this to do modular
    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedKey])

  // parse the txns and format for UI
  useEffect(() => {
    if (transactions && transactions.mints && transactions.burns && transactions.swaps) {
      let newTxns = []
      if (transactions.mints.length > 0) {
        transactions.mints.map((mint) => {
          let newTxn = {}
          newTxn.hash = mint.transaction.id
          newTxn.timestamp = mint.transaction.timestamp
          newTxn.type = TXN_TYPE.ADD
          newTxn.token0Amount = mint.amount0
          newTxn.token1Amount = mint.amount1
          newTxn.account = mint.to
          newTxn.token0Symbol = updateNameData(mint.pair).token0.symbol
          newTxn.token1Symbol = updateNameData(mint.pair).token1.symbol
          newTxn.token0Address = mint.pair.token0.id
          newTxn.token1Address = mint.pair.token1.id
          newTxn.amountUSD = mint.amountUSD
          return newTxns.push(newTxn)
        })
      }
      if (transactions.burns.length > 0) {
        transactions.burns.map((burn) => {
          let newTxn = {}
          newTxn.hash = burn.transaction.id
          newTxn.timestamp = burn.transaction.timestamp
          newTxn.type = TXN_TYPE.REMOVE
          newTxn.token0Amount = burn.amount0
          newTxn.token1Amount = burn.amount1
          newTxn.account = burn.sender
          newTxn.token0Symbol = updateNameData(burn.pair).token0.symbol
          newTxn.token1Symbol = updateNameData(burn.pair).token1.symbol
          newTxn.token0Address = burn.pair.token0.id
          newTxn.token1Address = burn.pair.token1.id
          newTxn.amountUSD = burn.amountUSD
          return newTxns.push(newTxn)
        })
      }
      if (transactions.swaps.length > 0) {
        transactions.swaps.map((swap) => {
          const netToken0 = swap.amount0In - swap.amount0Out
          const netToken1 = swap.amount1In - swap.amount1Out

          let newTxn = {}

          if (netToken0 < 0) {
            newTxn.token0Symbol = updateNameData(swap.pair).token0.symbol
            newTxn.token1Symbol = updateNameData(swap.pair).token1.symbol
            newTxn.token0Amount = Math.abs(netToken0)
            newTxn.token1Amount = Math.abs(netToken1)
          } else if (netToken1 < 0) {
            newTxn.token0Symbol = updateNameData(swap.pair).token1.symbol
            newTxn.token1Symbol = updateNameData(swap.pair).token0.symbol
            newTxn.token0Amount = Math.abs(netToken1)
            newTxn.token1Amount = Math.abs(netToken0)
          }

          newTxn.token0Address = swap.pair.token0.id
          newTxn.token1Address = swap.pair.token1.id

          newTxn.hash = swap.transaction.id
          newTxn.timestamp = swap.transaction.timestamp
          newTxn.type = TXN_TYPE.SWAP

          newTxn.amountUSD = swap.amountUSD
          newTxn.account = swap.to
          newTxn.sender = swap.sender
          return newTxns.push(newTxn)
        })
      }

      const filtered = newTxns.filter((item) => {
        if (txFilter !== TXN_TYPE.ALL) {
          return item.type === txFilter
        }
        return true
      })
      setFilteredItems(filtered)
      let extraPages = 1
      if (filtered.length % ITEMS_PER_PAGE === 0) {
        extraPages = 0
      }
      if (filtered.length === 0) {
        setMaxPage(1)
      } else {
        setMaxPage(Math.floor(filtered.length / ITEMS_PER_PAGE) + extraPages)
      }
    }
  }, [transactions, txFilter])

  useEffect(() => {
    setPage(1)
  }, [txFilter])

  const filteredList =
    filteredItems &&
    filteredItems
      .sort((a, b) => {
        return parseFloat(a[sortedColumn]) > parseFloat(b[sortedColumn])
          ? (sortDirection ? -1 : 1) * 1
          : (sortDirection ? -1 : 1) * -1
      })
      .slice(ITEMS_PER_PAGE * (page - 1), page * ITEMS_PER_PAGE)

  const handleNextPage = async () => {
    if (loadingMoreRef.current) {
      return
    }
    if (page < maxPage) {
      setPage(page + 1)
      return
    }
    if (hasMore && onLoadMore) {
      loadingMoreRef.current = true
      setLoadingMore(true)
      await onLoadMore()
      loadingMoreRef.current = false
      setLoadingMore(false)
      setPage((p) => p + 1)
    }
  }

  // Picking a range is a fresh, independent action - it always shows newest-first,
  // regardless of whatever direction a previous "Oldest first" toggle left behind.
  // The toggle is there afterward if oldest-first is what you actually want for this range.
  const applyRange = async (from, to, presetLabel, fromDateValue, toDateValue) => {
    if (!onSetDateRange || !isValidRange(from, to)) {
      return
    }
    setFromDate(fromDateValue)
    setToDate(toDateValue)
    setActivePreset(presetLabel ?? null)
    setAppliedRange({ from, to })
    setSortedColumn(SORT_FIELD.TIMESTAMP)
    setSortDirection(true)
    setPage(1)
    await onSetDateRange(from, to, 'desc')
  }

  const handleApplyDateRange = async () => {
    if (!fromDate || !toDate) {
      return
    }
    await applyRange(startOfDayUnix(fromDate), endOfDayExclusiveUnix(toDate), null, fromDate, toDate)
  }

  const handlePresetRange = async (preset) => {
    const { from, to } = computePresetRange(preset)
    await applyRange(from, to, preset.label, new Date(from * 1000), new Date(to * 1000))
  }

  // Land on the 3D range by default rather than the full unbounded feed - only once per
  // feed (mirrors the feedKey reset above), and only when a range filter is actually offered.
  const hasAppliedDefaultRangeRef = useRef(false)
  useEffect(() => {
    hasAppliedDefaultRangeRef.current = false
  }, [feedKey])
  useEffect(() => {
    if (onSetDateRange && !isDateRangeFiltered && !hasAppliedDefaultRangeRef.current) {
      hasAppliedDefaultRangeRef.current = true
      handlePresetRange(DEFAULT_PRESET)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSetDateRange, feedKey])

  const handleClearDateRange = () => {
    setFromDate(null)
    setToDate(null)
    setActivePreset(null)
    setAppliedRange(null)
    setPage(1)
    if (onClearDateRange) {
      onClearDateRange()
    }
  }

  // Flips actual fetch direction (re-walking from the real start/end of history, or of the
  // applied date range, forward/backward) - not just re-sorting whatever's already loaded.
  const handleToggleSortDirection = async () => {
    const newSortDirection = sortedColumn === SORT_FIELD.TIMESTAMP ? !sortDirection : true
    const newDirection = newSortDirection ? 'desc' : 'asc'
    setSortedColumn(SORT_FIELD.TIMESTAMP)
    setSortDirection(newSortDirection)
    setPage(1)
    if (isDateRangeFiltered && appliedRange && onSetDateRange) {
      await onSetDateRange(appliedRange.from, appliedRange.to, newDirection)
    } else if (onSetDirection) {
      await onSetDirection(newDirection)
    }
  }

  const below1080 = useMedia('(max-width: 1080px)')
  const below780 = useMedia('(max-width: 780px)')

  const ListItem = ({ item }) => {
    const account =
      item.account.toLowerCase() === energiswapRouterImplAddress.toLowerCase() ? item.sender : item.account
    return (
      <DashGrid style={{ height: '48px' }}>
        <DataText area="txn" fontWeight="500">
          <Link color={color} external href={urls.showTransaction(item.hash)}>
            {getTransactionType(item.type, item.token1Symbol, item.token0Symbol)}
          </Link>
        </DataText>
        <DataText area="value">
          {currency === 'NRG' ? formattedNum(item.valueNRG) : formattedNum(item.amountUSD, true)}
        </DataText>
        {!below780 && (
          <>
            <DataText area="amountOther">
              {formattedNum(item.token1Amount) + ' '}{' '}
              <FormattedName text={item.token1Symbol} maxCharacters={5} margin={true} />
            </DataText>
            <DataText area="amountToken">
              {formattedNum(item.token0Amount) + ' '}{' '}
              <FormattedName text={item.token0Symbol} maxCharacters={5} margin={true} />
            </DataText>
          </>
        )}
        {!below1080 && (
          <DataText area="account">
            <Link color={color} external href={EXPLORER_URL + '/address/' + account}>
              {account && account.slice(0, 6) + '...' + account.slice(38, 42)}
            </Link>
          </DataText>
        )}
        <DataText area="time">{formatTime(item.timestamp)}</DataText>
      </DashGrid>
    )
  }

  return (
    <>
      {(onSetDateRange || onClearDateRange) && (
        <FilterPanel>
          <FilterRow>
            <FilterGroup>
              <FilterLabel>Range:</FilterLabel>
              {PRESET_RANGES.map((preset) => (
                <PresetButton
                  key={preset.label}
                  active={activePreset === preset.label}
                  disabled={loadingMore || isLoadingInitial}
                  onClick={() => handlePresetRange(preset)}
                >
                  {preset.label}
                </PresetButton>
              ))}
              {isDateRangeFiltered && (
                <SmallButton disabled={loadingMore || isLoadingInitial} onClick={handleClearDateRange}>
                  Clear
                </SmallButton>
              )}
            </FilterGroup>
            <FilterGroup>
              <DatePicker
                value={fromDate}
                placeholder="From"
                onChange={(d) => {
                  setFromDate(d)
                  setActivePreset(null)
                }}
              />
              <FilterLabel>–</FilterLabel>
              <DatePicker
                value={toDate}
                placeholder="To"
                onChange={(d) => {
                  setToDate(d)
                  setActivePreset(null)
                }}
              />
              <SmallButton
                disabled={!fromDate || !toDate || loadingMore || isLoadingInitial}
                onClick={handleApplyDateRange}
              >
                Apply
              </SmallButton>
              <PresetButton disabled={loadingMore || isLoadingInitial} onClick={handleToggleSortDirection}>
                {sortedColumn === SORT_FIELD.TIMESTAMP && !sortDirection ? 'Oldest first ↑' : 'Newest first ↓'}
              </PresetButton>
            </FilterGroup>
          </FilterRow>
        </FilterPanel>
      )}
      <DashGrid center={true} style={{ height: 'fit-content', padding: '0 0 1rem 0' }}>
        {below780 ? (
          <RowBetween area="txn">
            <DropdownSelect options={TXN_TYPE} active={txFilter} setActive={setTxFilter} color={color} />
          </RowBetween>
        ) : (
          <RowFixed area="txn" gap="10px" pl={4}>
            <SortText
              onClick={() => {
                setTxFilter(TXN_TYPE.ALL)
              }}
              active={txFilter === TXN_TYPE.ALL}
            >
              All
            </SortText>
            <SortText
              onClick={() => {
                setTxFilter(TXN_TYPE.SWAP)
              }}
              active={txFilter === TXN_TYPE.SWAP}
            >
              Swaps
            </SortText>
            <SortText
              onClick={() => {
                setTxFilter(TXN_TYPE.ADD)
              }}
              active={txFilter === TXN_TYPE.ADD}
            >
              Adds
            </SortText>
            <SortText
              onClick={() => {
                setTxFilter(TXN_TYPE.REMOVE)
              }}
              active={txFilter === TXN_TYPE.REMOVE}
            >
              Removes
            </SortText>
          </RowFixed>
        )}

        <Flex alignItems="center" justifyContent="flexStart">
          <ClickableText
            area="value"
            onClick={(e) => {
              setSortedColumn(SORT_FIELD.VALUE)
              setSortDirection(sortedColumn !== SORT_FIELD.VALUE ? true : !sortDirection)
            }}
          >
            Total Value {sortedColumn === SORT_FIELD.VALUE ? (!sortDirection ? '↑' : '↓') : ''}
          </ClickableText>
        </Flex>
        {!below780 && (
          <Flex alignItems="center">
            <ClickableText
              area="amountToken"
              onClick={() => {
                setSortedColumn(SORT_FIELD.AMOUNT0)
                setSortDirection(sortedColumn !== SORT_FIELD.AMOUNT0 ? true : !sortDirection)
              }}
            >
              {symbol0Override ? symbol0Override + ' Amount' : 'Token Amount'}{' '}
              {sortedColumn === SORT_FIELD.AMOUNT0 ? (sortDirection ? '↑' : '↓') : ''}
            </ClickableText>
          </Flex>
        )}
        <>
          {!below780 && (
            <Flex alignItems="center">
              <ClickableText
                area="amountOther"
                onClick={() => {
                  setSortedColumn(SORT_FIELD.AMOUNT1)
                  setSortDirection(sortedColumn !== SORT_FIELD.AMOUNT1 ? true : !sortDirection)
                }}
              >
                {symbol1Override ? symbol1Override + ' Amount' : 'Token Amount'}{' '}
                {sortedColumn === SORT_FIELD.AMOUNT1 ? (sortDirection ? '↑' : '↓') : ''}
              </ClickableText>
            </Flex>
          )}
          {!below1080 && (
            <Flex alignItems="center">
              <TYPE.body area="account">Account</TYPE.body>
            </Flex>
          )}
          <Flex alignItems="center">
            <ClickableText
              area="time"
              onClick={() => {
                setSortedColumn(SORT_FIELD.TIMESTAMP)
                setSortDirection(sortedColumn !== SORT_FIELD.TIMESTAMP ? true : !sortDirection)
              }}
            >
              Time {sortedColumn === SORT_FIELD.TIMESTAMP ? (!sortDirection ? '↑' : '↓') : ''}
            </ClickableText>
          </Flex>
        </>
      </DashGrid>
      <Divider />
      <List p={0}>
        {!filteredList || (isLoadingInitial && filteredList.length === 0) ? (
          <LocalLoader />
        ) : filteredList.length === 0 ? (
          <EmptyCard>No recent transactions found.</EmptyCard>
        ) : (
          filteredList.map((item, index) => {
            return (
              <div key={index}>
                <ListItem key={index} index={index + 1} item={item} />
                <Divider />
              </div>
            )
          })
        )}
      </List>
      <PageButtons>
        <div
          onClick={(e) => {
            setPage(page === 1 ? page : page - 1)
          }}
        >
          <Arrow faded={page === 1 ? true : false}>←</Arrow>
        </div>
        <TYPE.body>{loadingMore ? 'Loading more...' : 'Page ' + page + ' of ' + maxPage}</TYPE.body>
        <div onClick={handleNextPage}>
          <Arrow faded={page === maxPage && !hasMore ? true : false}>→</Arrow>
        </div>
      </PageButtons>
    </>
  )
}

export default TxnList
